import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Brushstroke-style showcase rows — image cut into an organic blob shape
 * with bold marketing copy beside it. Alternates left/right per row.
 *
 * Inspired by the reference layout (photo + colored brushstroke + heading).
 */

const ROWS = [
  {
    id: 'shelf',
    image: '/images/features/listen.jpg',
    imageAlt: 'A child peeking out of a book fort built from stacks of children’s storybooks',
    blob: 'violet',
    eyebrow: 'Built for bedtime',
    title: 'Read books, save favourites, build playlists',
    body: 'Give your child an easy way to enjoy and organise their reading. With DeenLit, they can open a book, save the stories they love, build playlists for bedtime or study time, and return to their favourites whenever they want — helping reading become a regular part of their day.',
    cta: { label: 'Start your shelf', to: '/profile' },
  },
  {
    id: 'narrator',
    image: '/images/features/spark.jpg',
    imageAlt: 'Headphones, a stack of books and a microphone on a desk',
    blob: 'coral',
    eyebrow: 'Hear every page',
    title: 'A storyteller built into every book.',
    body: 'Each page is narrated in English and Arabic with words lighting up as they’re read aloud. A smooth page slider lets little hands set the pace — perfect for reading along.',
    cta: { label: 'Open a story', to: '/category/all' },
  },
  {
    id: 'achievements',
    image: '/images/features/heritage.jpg',
    imageAlt: 'A child smiling at a laptop screen',
    blob: 'teal',
    eyebrow: 'Learn while they play',
    title: 'Quizzes, badges & reading streaks.',
    body: 'Finish a story, take a quick quiz, unlock an achievement. Every chapter your child reads adds to their streak — turning learning into something they’ll come back to.',
    cta: { label: 'See achievements', to: '/profile' },
  },
];

export default function HomeShowcase() {
  // Signed-in visitors go straight to the subscribe page; newcomers create an account first.
  const [signedIn, setSignedIn] = useState(() => { try { return Boolean(localStorage.getItem('adeeb-token')); } catch { return false; } });
  useEffect(() => {
    const refresh = () => { try { setSignedIn(Boolean(localStorage.getItem('adeeb-token'))); } catch { setSignedIn(false); } };
    window.addEventListener('storage', refresh); window.addEventListener('adeeb-auth-change', refresh);
    return () => { window.removeEventListener('storage', refresh); window.removeEventListener('adeeb-auth-change', refresh); };
  }, []);
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`home-showcase ${visible ? 'is-visible' : ''}`}
      aria-label="What Deenlit brings to your child"
    >
      {ROWS.map((row, i) => (
        <article
          key={row.id}
          className={`showcase-row b-${row.blob} ${i % 2 === 1 ? 'is-reversed' : ''}`}
          style={{ '--row-delay': `${0.1 * i}s` }}
        >
          <div className="showcase-art">
            {/* Painted brushstroke blob behind the image */}
            <svg
              className="showcase-blob"
              viewBox="0 0 600 460"
              aria-hidden="true"
              preserveAspectRatio="none"
            >
              <path
                className="showcase-blob-path"
                d="M76,86 C140,28 254,12 348,28 C440,42 528,86 568,170 C606,254 590,340 510,394 C420,452 312,442 220,420 C124,396 38,344 22,250 C8,166 24,140 76,86 Z"
              />
            </svg>

            {/* Floating accent dots */}
            <span className="showcase-dot showcase-dot-1" aria-hidden="true" />
            <span className="showcase-dot showcase-dot-2" aria-hidden="true" />
            <span className="showcase-dot showcase-dot-3" aria-hidden="true" />

            {/* The photo */}
            <div className="showcase-photo">
              <img src={row.image} alt={row.imageAlt} loading="lazy" />
            </div>
          </div>

          <div className="showcase-copy">
            <div className="showcase-eyebrow">
              <span className="showcase-eyebrow-bar" />
              {row.eyebrow}
            </div>
            <h3 className="showcase-title">{row.title}</h3>
            <p className="showcase-body">{row.body}</p>
            <Link to={row.cta.to} className="showcase-cta">
              {row.cta.label}
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
              </svg>
            </Link>
          </div>
        </article>
      ))}

      {/* Join CTA — two themed cards (parent + school) so the homepage
       * closes with two clear, equal-weight paths in. */}
      <div className={`showcase-join${visible ? ' is-visible' : ''}`}>
        <div className="showcase-join-ornament" aria-hidden="true">&#10040; &#10040; &#10040;</div>
        <h3 className="showcase-join-title">Ready to start reading?</h3>
        <p className="showcase-join-body">
          Pick what fits — a personal family account, or a whole school.
        </p>

        <div className="showcase-join-cards">
          <Link to={signedIn ? '/subscribe' : '/register?redirect=%2Fsubscribe'} className="showcase-join-card showcase-join-card-parent">
            <div className="showcase-join-card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 21s-7-4.35-9.33-9.06C1.05 8.46 3.36 5 7 5c2.04 0 3.32 1.02 5 3 1.68-1.98 2.96-3 5-3 3.64 0 5.95 3.46 4.33 6.94C19 16.65 12 21 12 21z" />
              </svg>
            </div>
            <div className="showcase-join-card-eyebrow">For families</div>
            <div className="showcase-join-card-name">Join as a parent</div>
            <div className="showcase-join-card-pitch">Up to 6 reader profiles · bookmarks &amp; progress that follow your kids everywhere.</div>
            <div className="showcase-join-card-price">From <strong>£7.50</strong> / month</div>
            <span className="showcase-join-card-cta">
              Get started
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
              </svg>
            </span>
          </Link>

          <Link to="/school-join" className="showcase-join-card showcase-join-card-school">
            <div className="showcase-join-card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5a6 3 0 0 0 12 0v-5" />
              </svg>
            </div>
            <div className="showcase-join-card-eyebrow">For schools</div>
            <div className="showcase-join-card-name">Join as a school</div>
            <div className="showcase-join-card-pitch">Class &amp; student rosters, PIN sign-ins, bulk book assignments — DeenLit Madrasa, Academy or Institute.</div>
            <div className="showcase-join-card-price">From <strong>£199</strong> / year</div>
            <span className="showcase-join-card-cta">
              See plans
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
              </svg>
            </span>
          </Link>
        </div>

        {/* Reassurance row — three quick trust signals below the join
         * cards. Decorative-only icons (aria-hidden), so the screen
         * reader hears just the labels. */}
        <ul className="showcase-trust-row" aria-label="Why families choose Deenlit">
          <li className="showcase-trust-item">
            <span className="showcase-trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l8 3v6c0 4.5-3.4 8.6-8 9-4.6-.4-8-4.5-8-9V6l8-3z" />
                <polyline points="9 12 11.2 14.2 15 10.4" />
              </svg>
            </span>
            <div className="showcase-trust-text">
              <strong>Safe &amp; ad-free</strong>
              <span>A trusted space for kids</span>
            </div>
          </li>

          <li className="showcase-trust-item">
            <span className="showcase-trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="13" height="10" rx="1.5" />
                <rect x="16" y="8" width="5" height="11" rx="1" />
                <line x1="6" y1="19" x2="13" y2="19" />
                <line x1="9.5" y1="15" x2="9.5" y2="19" />
              </svg>
            </span>
            <div className="showcase-trust-text">
              <strong>Read anywhere</strong>
              <span>On web, tablet and mobile</span>
            </div>
          </li>

          <li className="showcase-trust-item">
            <span className="showcase-trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3h12v18l-6-3-6 3V3z" />
                <line x1="9" y1="8" x2="15" y2="8" />
              </svg>
            </span>
            <div className="showcase-trust-text">
              <strong>Islamic books kids love</strong>
              <span>Stories that inspire</span>
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
